import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { RefreshCw, Loader2, CheckCircle2, BellRing, Save } from "lucide-react";
import { toast } from "sonner";

const fmtDT = (v) => (v ? `${new Date(v).toLocaleDateString("it-IT")} ${new Date(v).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : "—");
const fmtD = (v) => { const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : (v || "—"); };

function AvvisoConfig() {
  const [giorni, setGiorni] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/rinnovo-config").then(({ data }) => setGiorni(String(data.giorni))).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/rinnovo-config", { giorni: Number(giorni) }); setGiorni(String(data.giorni)); toast.success("Giorni di preavviso salvati"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="pgp-card p-6 max-w-4xl mb-6" data-testid="rinnovo-config">
      <h3 className="font-head font-semibold text-slate-100 mb-1">Preavviso scadenza abbonamento</h3>
      <p className="text-sm text-slate-400 mb-3">Numero di giorni prima della scadenza da cui gli studi vedono l'avviso all'accesso e nella scheda Abbonamento.</p>
      <div className="flex items-center gap-2">
        <Input type="number" min={1} max={365} value={giorni} onChange={(e) => setGiorni(e.target.value)} data-testid="rinnovo-config-giorni" className="w-28 bg-slate-900/60 border-slate-700" />
        <span className="text-sm text-slate-400">giorni</span>
        <Button onClick={save} disabled={busy || !giorni} data-testid="rinnovo-config-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Salva
        </Button>
      </div>
    </div>
  );
}

function RichiestaRow({ r, onDone }) {
  return (
    <tr className="border-b border-slate-800/70" data-testid={`rinnovo-row-${r.id}`}>
      <td className="py-2.5 pr-3 whitespace-nowrap text-slate-300">{fmtDT(r.created_at)}</td>
      <td className="py-2.5 pr-3">
        <div className="font-semibold text-slate-100">{r.studio_name || "—"}</div>
        <div className="text-xs text-slate-500">{r.codice_studio ? `Cod. ${r.codice_studio}` : ""}</div>
      </td>
      <td className="py-2.5 pr-3 text-slate-300">{r.piano || "—"}<div className="text-xs text-slate-500">Scad. {fmtD(r.data_scadenza)}</div></td>
      <td className="py-2.5 pr-3 text-xs text-slate-400">
        <div>{r.richiedente_nome}{r.richiedente_email ? ` · ${r.richiedente_email}` : ""}</div>
        <div>{[r.email, r.telefono].filter(Boolean).join(" · ")}</div>
      </td>
      <td className="py-2.5 text-right">
        {r.gestita ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-400" data-testid={`rinnovo-gestita-${r.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Gestita</span>
        ) : (
          <Button size="sm" onClick={() => onDone(r.id)} data-testid={`rinnovo-segna-${r.id}`} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white">Segna come gestita</Button>
        )}
      </td>
    </tr>
  );
}

export default function RichiesteRinnovo() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/admin/rinnovo-richieste").then(({ data }) => { setRows(data || []); window.dispatchEvent(new Event("pgp:rinnovo-changed")); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const segna = async (id) => {
    try { await api.post(`/admin/rinnovo-richieste/${id}/gestita`); setRows((rs) => rs.map((r) => (r.id === id ? { ...r, gestita: true } : r))); window.dispatchEvent(new Event("pgp:rinnovo-changed")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const nuove = rows.filter((r) => !r.gestita).length;

  return (
    <div data-testid="richieste-rinnovo-page">
      <PageHeader title="Richieste Rinnovo" subtitle="Richieste di rinnovo abbonamento inviate dagli studi." icon={BellRing}
        actions={
          <Button onClick={load} disabled={loading} variant="outline" data-testid="rinnovo-refresh" className="border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white">
            {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Aggiorna
          </Button>
        } />
      <AvvisoConfig />
      <div className="pgp-card p-6 max-w-4xl" data-testid="rinnovo-list">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="font-head font-semibold text-slate-100">Richieste</h3>
          <span className={`text-xs rounded-full px-2 py-0.5 ${nuove ? "bg-amber-500 text-[#0a0d14]" : "bg-slate-800 text-slate-400"}`} data-testid="rinnovo-count-nuove">{nuove} nuove</span>
        </div>
        {!rows.length ? (
          <p className="text-sm text-slate-500 py-6 text-center" data-testid="rinnovo-empty">{loading ? "Caricamento…" : "Nessuna richiesta di rinnovo."}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-800">
                <th className="py-2 pr-3">Data e ora</th><th className="py-2 pr-3">Studio</th><th className="py-2 pr-3">Piano</th><th className="py-2 pr-3">Contatti</th><th className="py-2" />
              </tr></thead>
              <tbody>{rows.map((r) => <RichiestaRow key={r.id} r={r} onDone={segna} />)}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
